import React, { useEffect, useRef, useState, useCallback } from "react";

/**
 * ManualHero — sección hero editorial del portal /academia/:token.
 * Patrón "Trascendencia": imagen full-bleed, dot grid, palabra decorativa,
 * scramble text, dos columnas. Personalizado con el nombre de la alumna
 * y con el progreso de unidades terminadas + descarga del PDF.
 */

const SCRAMBLE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function useScramble(target, { delay = 0, stepMs = 50, active = true }) {
  const [out, setOut] = useState("");
  useEffect(() => {
    if (!active) {
      setOut(target);
      return undefined;
    }
    let raf;
    let cancelled = false;
    const timer = setTimeout(() => {
      let progress = 0;
      const tick = () => {
        if (cancelled) return;
        const revealed = Math.floor(progress);
        const rand = Array.from({ length: Math.min(3, target.length - revealed) })
          .map(() => {
            const nextChar = target[revealed];
            if (nextChar === undefined || nextChar === " ") return nextChar || "";
            return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
          })
          .join("");
        setOut(target.slice(0, revealed) + rand);
        progress += 0.5;
        if (revealed < target.length) {
          raf = setTimeout(tick, stepMs);
        } else {
          setOut(target);
        }
      };
      tick();
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (raf) clearTimeout(raf);
    };
  }, [target, delay, stepMs, active]);
  return out;
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = (e) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

export default function ManualHero({
  student,
  subtitle,
  pdfUrl,
  completedCount = 0,
  totalCount = 10,
  onOpenToc,
  onExplore,
}) {
  const reduce = usePrefersReducedMotion();
  const active = !reduce;

  const l1 = useScramble("El", { delay: 200 + 800, active });
  const l2 = useScramble("método", { delay: 500 + 800, active });
  const r1 = useScramble("Edición", { delay: 700 + 800, active });
  const r2 = useScramble(`de ${student?.first_name || ""}`, { delay: 1000 + 800, active });

  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  const handleExplore = useCallback(() => {
    if (typeof onExplore === "function") return onExplore();
    const next = rootRef.current?.nextElementSibling;
    if (next && "scrollIntoView" in next) next.scrollIntoView({ behavior: "smooth" });
  }, [onExplore]);

  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <section
      ref={rootRef}
      className="manual-hero relative w-full overflow-hidden bg-black text-white"
      data-testid="manual-hero"
    >
      {/* ── Imagen de fondo ── */}
      <img
        src="/media/abril-hero-bridal.jpg"
        alt="Referencia visual · Método Abril Suárez"
        className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
        draggable={false}
      />
      {/* ── Oscurecido para legibilidad ── */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,.60) 0%, rgba(0,0,0,.40) 30%, rgba(0,0,0,.60) 100%)",
        }}
      />
      {/* ── Retícula de puntos 24px @ 5% ── */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* ── Palabra decorativa trasera ── */}
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
      >
        <span
          className="manual-decoration whitespace-nowrap uppercase leading-none"
          style={{
            fontSize: "clamp(120px, 30vw, 521px)",
            opacity: 0.1,
            backgroundImage:
              "radial-gradient(ellipse at center, rgba(255,255,255,1) 0%, rgba(255,255,255,.35) 55%, rgba(255,255,255,0) 80%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
          }}
        >
          TRASCENDENCIA
        </span>
      </div>

      {/* ── NAV ── */}
      <header
        className="manual-nav relative z-20 flex items-center justify-between px-5 sm:px-10 py-6"
        data-testid="manual-hero-nav"
      >
        <a
          href="/"
          className="manual-brand flex items-center gap-3 group focus:outline-none focus-visible:ring-1 focus-visible:ring-white px-1"
          data-testid="manual-hero-brand"
        >
          <span
            aria-hidden
            className="inline-block w-3.5 h-3.5 border border-white/80 rotate-45 group-hover:rotate-[135deg] transition-transform duration-500"
          />
          <span className="uppercase text-[11px] tracking-[0.28em] font-bold">Método · X</span>
        </a>

        <div className="hidden sm:flex items-center gap-8 text-[11px] tracking-[0.24em] uppercase text-white/85">
          <span data-testid="manual-hero-version">
            MMXXVI · {(student?.first_name || "").toUpperCase()}
          </span>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="hover:text-white underline underline-offset-[6px] decoration-white/50 hover:decoration-white transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white px-1 py-1"
            data-testid="manual-hero-download"
          >
            Descargar
          </a>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 hover:text-white transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white px-1 py-1"
            aria-expanded={menuOpen}
            aria-label="Abrir menú"
            data-testid="manual-hero-menu"
          >
            <span className="flex flex-col gap-[3px]">
              <span className="block w-4 h-[1px] bg-white" />
              <span className="block w-4 h-[1px] bg-white" />
            </span>
            Menú
          </button>
        </div>

        {/* Mobile compact controls */}
        <div className="flex sm:hidden items-center gap-4">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="uppercase text-[10px] tracking-[0.22em] underline underline-offset-[5px] decoration-white/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
            data-testid="manual-hero-download-mobile"
          >
            Descargar
          </a>
          <button
            onClick={() => (onOpenToc ? onOpenToc() : setMenuOpen(true))}
            aria-label="Abrir menú"
            className="w-8 h-8 inline-flex flex-col items-center justify-center gap-[3px] focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
            data-testid="manual-hero-menu-mobile"
          >
            <span className="block w-4 h-[1px] bg-white" />
            <span className="block w-4 h-[1px] bg-white" />
          </button>
        </div>
      </header>

      {/* ── Contenido inferior en dos columnas ── */}
      <div
        className="relative z-10 flex items-end min-h-[calc(100vh-96px)] sm:min-h-[calc(100vh-120px)] px-5 sm:px-10 pb-12 sm:pb-16"
        style={{ minHeight: "calc(100svh - 96px)" }}
      >
        <div className="w-full grid md:grid-cols-2 gap-10 md:gap-16">
          {/* ── Columna izquierda: El / método ── */}
          <div className="text-left" data-testid="manual-hero-col-left">
            <p
              className="manual-title uppercase text-[11px] tracking-[0.28em] text-white/70 mb-6"
              data-testid="manual-hero-eyebrow"
            >
              Academia · Edición Alumna
            </p>
            <h1
              className="manual-title uppercase leading-[0.95] font-bold text-white"
              style={{
                fontSize: "clamp(40px, 10vw, 100px)",
                letterSpacing: "-0.03em",
              }}
            >
              <span className="block" data-testid="manual-hero-h1-l1">{l1}</span>
              <span aria-hidden className="block text-white/70" data-testid="manual-hero-h1-l2">
                <span className="opacity-40">/&nbsp;</span>{l2}
              </span>
              <span className="sr-only">El método</span>
            </h1>
            <p
              className="manual-copy mt-8 max-w-md text-white/85 text-[13px] sm:text-sm leading-relaxed"
              data-testid="manual-hero-copy-left"
            >
              {subtitle}. Diez unidades, cientos de horas de piel real. No un curso: un método.
              La firma de Abril Suárez se estudia desde la preparación de la piel hasta el
              último brillo en el labio, con criterio editorial y una ética profesional que no
              negocia.
            </p>
          </div>

          {/* ── Columna derecha: Edición / de {nombre} ── */}
          <div className="text-left md:text-right" data-testid="manual-hero-col-right">
            <h2
              className="manual-title uppercase leading-[0.95] font-bold text-white"
              style={{
                fontSize: "clamp(40px, 10vw, 100px)",
                letterSpacing: "-0.03em",
              }}
            >
              <span className="block" data-testid="manual-hero-h1-r1">{r1}</span>
              <span aria-hidden className="block text-white/70" data-testid="manual-hero-h1-r2">
                <span className="opacity-40">/&nbsp;</span>{r2}
              </span>
              <span className="sr-only">Edición de {student?.first_name}</span>
            </h2>

            {/* Progress bar */}
            <div className="mt-10 max-w-md md:ml-auto" data-testid="manual-hero-progress">
              <div className="flex items-baseline justify-between mb-2 text-[11px] tracking-[0.24em] uppercase">
                <span className="text-white/70">Tu progreso · {completedCount} / {totalCount}</span>
                <span className="text-white">{pct}%</span>
              </div>
              <div className="h-[3px] w-full bg-white/20 overflow-hidden">
                <div
                  className="h-full bg-[#8DBE9F] transition-all duration-700 ease-out"
                  style={{ width: `${pct}%` }}
                  data-testid="manual-hero-progress-bar"
                />
              </div>
            </div>

            <div className="mt-10 flex flex-wrap md:justify-end gap-3">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="uppercase text-[11px] tracking-[0.28em] bg-white text-black px-6 py-3 hover:bg-white/85 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
                data-testid="manual-hero-cta-primary"
              >
                Descargar PDF
              </a>
              <button
                onClick={handleExplore}
                className="uppercase text-[11px] tracking-[0.28em] border border-white/50 text-white px-6 py-3 hover:border-white transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
                data-testid="manual-hero-cta-secondary"
              >
                Explorar unidades
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Menú overlay */}
      {menuOpen && (
        <nav
          className="absolute inset-0 z-30 bg-black/95 flex items-center justify-center"
          onClick={() => setMenuOpen(false)}
          data-testid="manual-hero-menu-overlay"
        >
          <ul className="manual-title space-y-6 text-center uppercase text-white text-2xl sm:text-4xl">
            <li>
              <button
                onClick={() => { setMenuOpen(false); handleExplore(); }}
                className="tracking-[0.15em] text-white hover:text-white/70 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
              >
                Ver Unidades
              </button>
            </li>
            <li>
              <button
                onClick={() => { setMenuOpen(false); if (onOpenToc) onOpenToc(); }}
                className="tracking-[0.15em] text-white hover:text-white/70 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
              >
                Índice
              </button>
            </li>
            <li>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="tracking-[0.15em] text-white hover:text-white/70 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
              >
                Descargar PDF
              </a>
            </li>
            <li>
              <a
                href="/"
                className="tracking-[0.15em] text-white hover:text-white/70 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white text-lg sm:text-2xl opacity-70"
              >
                Volver al sitio
              </a>
            </li>
          </ul>
        </nav>
      )}

      <style>{`
        .manual-hero { min-height: 100vh; min-height: 100svh; }
        .manual-title, .manual-copy, .manual-nav, .manual-brand { font-family: 'Space Mono', ui-monospace, monospace; }
        .manual-decoration { font-family: 'Anton SC', 'Anton', sans-serif; }
        @media (prefers-reduced-motion: reduce) {
          .manual-hero * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </section>
  );
}
